/*!
 * AudioLogFormat — which container a segment is recorded in, what the file is
 * called and how big it will be.
 *
 * The file leaves the browser and is opened by whatever the person's device
 * has, so «the browser can record it» is not the question. Measured 13.10.2026
 * with each engine's own MediaRecorder, 8–12 s takes, read back by ffprobe, by
 * an <audio> element and by Windows Media Foundation (the decoder Windows
 * players use):
 *
 *   engine            container · codec     length in file   seek   Windows   Apple
 *   Chrome/Edge 155   mp4 · AAC-LC          yes              yes    opens     opens
 *   Chrome/Edge 155   webm · Opus           NO               no     opens     no
 *   Chrome/Edge 155   mp4 · Opus (plain     yes              yes    opens     no
 *                     «audio/mp4»!)
 *   Firefox 148       webm · Opus           NO               no     opens     no
 *   Firefox 148       ogg · Opus            yes              yes    refuses   no
 *   iPhone Safari 26  webm · Opus           — field, 05.10.2026: the Files app
 *                                             and its player open nothing
 *
 * So AAC in MP4 comes first wherever the engine records it (Chromium, Safari):
 * one file every player opens, with its length in it. A WebM is taken only
 * where there is no AAC recorder (Firefox, Chromium builds without the
 * encoder), and then it is finished after the segment closes — see
 * AudioLogFormat.finish — because a live WebM carries neither length nor index.
 *
 * Plain «audio/mp4» is ambiguous: Safari writes AAC into it, Chromium writes
 * Opus, and Opus in MP4 opens on neither Apple device. It is accepted only
 * from an engine that does not offer Opus-in-MP4 at all.
 *
 * Bitrate. Opus writes what is asked. Chromium's AAC encoder does not go below
 * ~100 kbit/s whatever is asked (64 → 100, 128 → 132, 192 → 196, mono and
 * stereo alike) — the size estimate uses the measured figure, not the request.
 *
 * window.AudioLogFormat
 *   pick(MediaRecorder)          → {mime, ext, family:'aac'|'opus', container}
 *   describe(mime)               → the same for a mime a recorder reported
 *   kbps(family, askedBps)       → what the file will really carry
 *   mbPerMin(family, askedBps)   → megabytes per minute of recording
 *   finish(blob, info)           → Promise<{blob, finished:boolean, durationMs?}>
 * Guard: tests/js/audio-log-format.test.mjs.
 */
(function (global) {
'use strict';
if (global && global.AudioLogFormat) return;

var AAC = 'audio/mp4;codecs=mp4a.40.2';
var AAC_FLOOR_KBPS = 100;

function describe(mime) {
    var m = String(mime || '').toLowerCase();
    var container = m.indexOf('mp4') !== -1 ? 'mp4' : m.indexOf('ogg') !== -1 ? 'ogg' : m.indexOf('webm') !== -1 ? 'webm' : '';
    var family = /mp4a|aac/.test(m) ? 'aac' : /opus|vorbis/.test(m) ? 'opus' : (container === 'mp4' ? 'aac' : 'opus');
    /* .m4a is the name every system ties to «audio in MP4»; .webm stays
       .webm — the bytes are WebM and __downloadFile checks names against bytes. */
    var ext = container === 'mp4' ? 'm4a' : container === 'ogg' ? 'ogg' : container === 'webm' ? 'webm' : 'bin';
    return { mime: mime || '', ext: ext, family: family, container: container };
}

function pick(MR) {
    MR = MR || (global && global.MediaRecorder);
    var can = function (t) { try { return !!(MR && MR.isTypeSupported && MR.isTypeSupported(t)); } catch (_) { return false; } };
    if (can(AAC)) return describe(AAC);
    /* An engine that records «audio/mp4» but not Opus-in-MP4 writes AAC there. */
    if (can('audio/mp4') && !can('audio/mp4;codecs=opus')) {
        var d = describe('audio/mp4'); d.family = 'aac'; return d;
    }
    if (can('audio/webm;codecs=opus')) return describe('audio/webm;codecs=opus');
    if (can('audio/webm')) return describe('audio/webm');
    if (can('audio/ogg;codecs=opus')) return describe('audio/ogg;codecs=opus');
    return describe('');      // the recorder's own default; described again from rec.mimeType
}

function kbps(family, askedBps) {
    var k = Math.round((askedBps || 64000) / 1000);
    return family === 'aac' ? Math.max(k, AAC_FLOOR_KBPS) + (k > AAC_FLOOR_KBPS ? 4 : 0) : k;
}

function mbPerMin(family, askedBps) {
    return kbps(family, askedBps) * 1000 / 8 * 60 / 1048576;
}

/* A live WebM has no Duration and no Cues: players show no length and cannot
   seek. WebmCopyTrim rewrites the skeleton over the same bytes (no decoding,
   no re-encoding; measured 14 ms on a short take, the body is referenced, not
   copied). MP4 and Ogg are complete as recorded. A failure leaves the segment
   as it was — an unfinished file still plays from the start. */
function finish(blob, info) {
    info = info || describe(blob && blob.type);
    var W = global && global.WebmCopyTrim;
    if (!blob || info.container !== 'webm' || !W || typeof W.run !== 'function') {
        return Promise.resolve({ blob: blob, finished: false });
    }
    var file;
    try { file = new File([blob], 'segment.webm', { type: blob.type || 'audio/webm' }); }
    catch (_) { return Promise.resolve({ blob: blob, finished: false }); }
    return W.run(file, 0, 1e9).then(function (out) {
        var ms = out && out.trimInfo ? out.trimInfo.durationMs : null;
        var typed = new Blob([out], { type: blob.type || 'audio/webm' });
        return { blob: typed, finished: true, durationMs: ms };
    }).catch(function (e) {
        try { if (global && global.__ae) global.__ae('feature_use', { f: 'al_finish_failed', slug: 'audio-logger', err: String((e && (e.code || e.name)) || e).slice(0, 40) }); } catch (_) {}
        return { blob: blob, finished: false };
    });
}

var api = { pick: pick, describe: describe, kbps: kbps, mbPerMin: mbPerMin, finish: finish, AAC: AAC, AAC_FLOOR_KBPS: AAC_FLOOR_KBPS };
if (typeof module !== 'undefined' && module.exports) module.exports = api;
if (global) global.AudioLogFormat = api;
})(typeof window !== 'undefined' ? window : (typeof self !== 'undefined' ? self : null));
